import React, { useRef, useMemo } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';

const rayVertexShader = `
varying vec2 vUv;
void main() {
    vUv = uv;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}
`;

const rayFragmentShader = `
uniform float uOpacity;
uniform float uPhase;
uniform float uTime;
varying vec2 vUv;

void main() {
    // Vertical gradient: bright at top (surface), fading down
    float gradient = smoothstep(0.0, 1.0, vUv.y);

    // Horizontal falloff: bright in center, fading at edges
    float horizontal = 1.0 - abs(vUv.x - 0.5) * 2.0;
    horizontal = smoothstep(0.0, 0.5, horizontal);

    // Animated shimmer
    float shimmer = sin(uTime * 0.5 + uPhase) * 0.3 + 0.7;
    float shimmer2 = sin(uTime * 0.3 + uPhase * 1.7) * 0.2 + 0.8;

    float alpha = gradient * horizontal * shimmer * shimmer2 * uOpacity;

    // Soft blue-white color for underwater light
    vec3 color = vec3(0.3, 0.6, 0.7);

    gl_FragColor = vec4(color, alpha * 0.15);
}
`;

const RAY_COUNT = 10;

function GodRays({ sunPosition, enabled = true }) {
  const groupRef = useRef();
  const raysRef = useRef([]);
  const timeRef = useRef(0);

  const rayData = useMemo(() => {
    const data = [];
    for (let i = 0; i < RAY_COUNT; i++) {
      data.push({
        phase: Math.random() * Math.PI * 2,
        offsetX: (Math.random() - 0.5) * 60,
        offsetZ: (Math.random() - 0.5) * 60,
        width: 3 + Math.random() * 5,
        height: 20 + Math.random() * 15,
        baseOpacity: 0.3 + Math.random() * 0.7,
      });
    }
    return data;
  }, []);

  const materials = useMemo(() => {
    return rayData.map(d => (
      new THREE.ShaderMaterial({
        uniforms: {
          uOpacity: { value: d.baseOpacity },
          uPhase: { value: d.phase },
          uTime: { value: 0 },
        },
        vertexShader: rayVertexShader,
        fragmentShader: rayFragmentShader,
        transparent: true,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
        side: THREE.DoubleSide,
      })
    ));
  }, [rayData]);

  useFrame((state, delta) => {
    if (!enabled || !groupRef.current) return;

    timeRef.current += delta;
    const camera = state.camera;
    const isUnderwater = camera.position.y < 0;

    groupRef.current.visible = isUnderwater;

    if (!isUnderwater) return;

    // Position rays around camera, offset toward sun
    const sunDir = sunPosition ? new THREE.Vector3(...sunPosition).normalize() : new THREE.Vector3(0, 1, 0);

    groupRef.current.position.set(
      camera.position.x + sunDir.x * 10,
      Math.min(camera.position.y + 5, -2),
      camera.position.z + sunDir.z * 10
    );

    // Update material uniforms
    materials.forEach(mat => {
      mat.uniforms.uTime.value = timeRef.current;
    });

    // Depth-based opacity: brighter near surface, fading at depth
    const depth = Math.max(0, -camera.position.y);
    const depthFade = Math.exp(-depth * 0.05);

    raysRef.current.forEach((mesh, i) => {
      if (!mesh) return;
      const d = rayData[i];
      materials[i].uniforms.uOpacity.value = d.baseOpacity * depthFade;

      // Billboarding: face camera on Y axis
      mesh.lookAt(camera.position);
    });
  });

  const planeGeom = useMemo(() => new THREE.PlaneGeometry(1, 1), []);

  return (
    <group ref={groupRef} visible={false}>
      {rayData.map((d, i) => (
        <mesh
          key={i}
          ref={el => { raysRef.current[i] = el; }}
          geometry={planeGeom}
          material={materials[i]}
          position={[d.offsetX, d.height * 0.5, d.offsetZ]}
          scale={[d.width, d.height, 1]}
        />
      ))}
    </group>
  );
}

export default GodRays;
